import { UnitTestConfiguration } from "../../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";
import { Box, Link, TextField } from "@mui/material";
import { UILabel } from "../BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { UnitTestEvaluationType } from "../../rule-engine-core/Common/Testing/UnitTestEvaluationType";
import React, { useEffect, useState } from "react";
import { ValidationIssue } from "../../rule-engine-core/Common/IDesignValidationProvider";
import { OpenBlueIcon } from "../Icon/OpenBlueIcon";
import { DropdownBlueIcon } from "../Icon/DropdownBlueIcon";
import styled from "styled-components";
import { UISwitch } from "../BuildlingBlocks/UISwitch";
import { UnitTestResultNode } from "../../rule-engine-core/Common/UnitTesting/UnitTestResultNode";
import { UIStandardLayer } from "../BuildlingBlocks/UIStandardLayer";
import { UIToggleButtons } from "../BuildlingBlocks/UIToggleButtons";

export function DefineUnitTest(props: {
    open: boolean;
    onClose: () => void;
    onTestSave: (test: UnitTestConfiguration) => void;
    unitTest: UnitTestConfiguration;
}) {
    const [testName, setTestName] = useState<string>();
    const [expectError, setExpectError] = useState<boolean>(false);
    const [outcomesMatch, setOutcomesMatch] = useState<boolean>(true);
    const [selectEvents, setSelectEvents] = useState<boolean>(false);

    useEffect(() => {
        let testItems: UnitTestResultNode[] = [props.unitTest.resultNode];

        setSelectEvents(false);
        while (testItems.length !== 0) {
            let ti = testItems.pop();

            if (!ti) break;

            if (ti.unitTestEvaluationType !== UnitTestEvaluationType.NONE) {
                setSelectEvents(true);
                break;
            }

            if (ti.children) testItems.push(...ti.children);
        }

        setTestName(props.unitTest.name);
    }, [props.unitTest]);

    function getErrors(): ValidationIssue[] {
        let issues: ValidationIssue[] = [];

        if (!testName || testName === "") issues.push(new ValidationIssue("Unit Test Name Is Required"));

        return issues;
    }

    return (
        <UIStandardLayer
            primaryActionText={"Create Unit Test"}
            validationIssues={getErrors()}
            onPrimaryAction={() => {
                // create it here
                props.onTestSave(props.unitTest);
            }}
            open={props.open}
            onClose={props.onClose}
            title={"Define Unit Test"}
            renderUI={() => {
                return (
                    <Box
                        width={"550px"}
                        display={"flex"}
                        flexDirection={"column"}
                    >
                        <Box
                            paddingBottom={"10px"}
                            display={"flex"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                        >
                            <Box
                                display={"flex"}
                                alignItems={"center"}
                            >
                                <UILabel paddingRight={"15px"}>Unit Test Name:</UILabel>
                                <TextField
                                    value={testName}
                                    onChange={(e) => setTestName(e.target.value)}
                                    variant={"outlined"}
                                ></TextField>
                            </Box>
                            <Link>View Input Data</Link>
                        </Box>
                        <Box
                            marginTop={"10px"}
                            display={"flex"}
                        >
                            <Box width={"150px"}>
                                <UILabel>Select Events</UILabel>
                            </Box>
                            <UISwitch
                                value={selectEvents}
                                onChange={(v) => {
                                    setSelectEvents(v);
                                }}
                            ></UISwitch>
                        </Box>

                        {selectEvents && (
                            <Box
                                marginTop={"10px"}
                                paddingLeft={"5px"}
                                paddingRight={"10px"}
                                paddingTop={"10px"}
                                border={1}
                                borderColor={UIStyleConstants.GRAY_LIGHTER_COLOR}
                                borderRadius="4pxpx"
                                overflow={"hidden"}
                                height={"400px"}
                                style={{ overflowY: "scroll" }}
                            >
                                <TestItem
                                    i={props.unitTest.resultNode}
                                    startCollapsed={false}
                                />
                            </Box>
                        )}

                        <Box
                            marginTop={"10px"}
                            display={"flex"}
                        >
                            <Box width={"150px"}>
                                <UILabel>Error Expected</UILabel>
                            </Box>
                            <UISwitch
                                value={expectError}
                                onChange={(v) => {
                                    setExpectError(v);
                                }}
                            ></UISwitch>
                        </Box>
                        <Box
                            marginTop={"10px"}
                            display={"flex"}
                        >
                            <Box width={"150px"}>
                                <UILabel>Match Outputs</UILabel>
                            </Box>
                            <UISwitch
                                value={outcomesMatch}
                                onChange={(v) => {
                                    setOutcomesMatch(v);
                                }}
                            ></UISwitch>
                        </Box>
                    </Box>
                );
            }}
        />
    );
}

const ExpandIconButton = styled.div<{ visible: boolean }>`
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    visibility: ${(props) => (props.visible ? "visible" : "hidden")};
`;

function TestItem(props: { i: UnitTestResultNode; startCollapsed: boolean }) {
    const [resultType, setResultType] = useState<UnitTestEvaluationType>();

    const [collapsed, setCollapsed] = useState<boolean>(true);

    useEffect(() => {
        setResultType(props.i.unitTestEvaluationType);
        setCollapsed(props.startCollapsed);
    }, [props.i]);

    return (
        <Box
            key={"test_item:" + props.i.id}
            display={"flex"}
            flexDirection={"column"}
        >
            <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
            >
                <Box>
                    <Box
                        paddingRight={"15px"}
                        display={"flex"}
                        flexDirection={"column"}
                    >
                        <Box
                            display={"flex"}
                            alignItems={"center"}
                        >
                            <ExpandIconButton
                                visible={props.i.children !== undefined && props.i.children.length !== 0}
                                onClick={() => setCollapsed(!collapsed)}
                            >
                                {collapsed ? <OpenBlueIcon /> : <DropdownBlueIcon />}
                            </ExpandIconButton>

                            <Box
                                width={"10px"}
                                height={"10px"}
                                marginRight={"7px"}
                                marginLeft={"7px"}
                                bgcolor={
                                    props.i.isError ? UIStyleConstants.RED_COLOR : UIStyleConstants.GRAY_DARK_COLOR
                                }
                            ></Box>
                            <UILabel>{props.i.type}</UILabel>
                        </Box>
                        <Box
                            display={"flex"}
                            paddingLeft={"25px"}
                        >
                            <UILabel>{props.i.value}</UILabel>
                        </Box>
                    </Box>
                </Box>
                {resultType && (
                    <UIToggleButtons
                        options={[
                            { value: UnitTestEvaluationType.NONE, text: "Ignore" },
                            { value: UnitTestEvaluationType.EXISTS, text: "Exists" },
                            { value: UnitTestEvaluationType.DATA_MATCHES, text: "Matches" },
                        ]}
                        onChange={(v) => {
                            // @ts-ignore
                            setResultType(v);
                            props.i.unitTestEvaluationType = v as UnitTestEvaluationType;
                        }}
                        value={resultType}
                    />
                )}
            </Box>
            {!collapsed && (
                <Box paddingLeft={"10px"}>
                    {props.i.children?.map((ii: UnitTestResultNode) => {
                        return (
                            <TestItem
                                i={ii}
                                startCollapsed={true}
                            />
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}
